import { useRef, useState } from "react";
import moment from "moment";
import { useToast } from "@chakra-ui/react";
import type { Job } from "@models/Job";
import type { JobSchedule, ScheduleJobApplications, ScheduleMatchingConsultant } from "@models/Schedule";
import WeekSchedule, { WeekScheduleHandle } from "./WeekSchedule";
import { getConsultantName } from "./ApplicantList";
import { getExtensionConsultant, WeekScheduleDraft, type WeekScheduleShift } from "./weekSchedule.utils";

const day = (offset: number, time: string) =>
	moment()
		.startOf("isoWeek")
		.add(offset, "day")
		.set({ hour: Number(time.split(":")[0]), minute: Number(time.split(":")[1]) })
		.toISOString();

const shift = (id: string, offset: number, from: string, to: string) => ({ id, start: day(offset, from), end: day(offset, to) });

const initialJobs: JobSchedule[] = [
	{
		id: "job-1",
		orderNumber: "39947",
		status: "BOOKED_WITH_UNMANNED_SHIFTS",
		customer: { id: "c1", name: "Solrosens förskola" },
		competence: { branch: "school", service: "preschool-teacher" },
		contactPersons: [],
		startDate: day(0, "09:00"),
		endDate: day(11, "14:00"),
		shifts: [
			shift("s1", 0, "09:00", "14:00"),
			shift("s2", 1, "09:00", "14:00"),
			shift("s3", 2, "09:00", "14:00"),
			shift("s4", 3, "09:00", "14:00"),
			shift("s5", 4, "08:00", "13:00"),
			shift("s6", 7, "09:00", "14:00"),
			shift("s7", 8, "09:00", "14:00"),
		],
		applications: [
			{
				id: "a1",
				consultant: { id: "u1", firstName: "Elina", lastName: "Mannerstål", absences: [] },
				shifts: [{ id: "s1" }, { id: "s3" }, { id: "s4" }, { id: "s5" }],
			},
		],
	},
	{
		id: "job-2",
		orderNumber: "17953",
		status: "BOOKED",
		customer: { id: "c2", name: "Ekbackens skola" },
		competence: { branch: "school", service: "cook" },
		contactPersons: [],
		startDate: day(1, "07:00"),
		endDate: day(9, "16:00"),
		shifts: [
			shift("s8", 1, "07:00", "16:00"),
			shift("s9", 2, "07:00", "16:00"),
			shift("s10", 9, "07:00", "16:00"),
			// An extension the consultant declined: the shift keeps its status, the invitation tells
			{ ...shift("s11", 10, "07:00", "16:00"), status: "EXTEND_REQUESTED" } as WeekScheduleShift,
		],
		applications: [
			{
				id: "a2",
				consultant: {
					id: "u2",
					firstName: "Johan",
					lastName: "Berg",
					absences: [{ id: "abs1", startDate: day(2, "00:00"), endDate: day(2, "23:59"), type: "SICK_LEAVE" }],
				},
				shifts: [{ id: "s8" }, { id: "s9" }, { id: "s10" }],
			},
		],
	},
];

const detailsByJobId = {
	"job-1": {
		description: "Vikarie på avdelning Humlan, barn 3–5 år. Anmäl dig i receptionen vid ankomst.",
		shifts: initialJobs[0].shifts.map((s) => ({ ...s, break: 0.5 })),
	},
	"job-2": {
		description: "Lunchservering för ca 300 elever. Specialkost förekommer.",
		shifts: initialJobs[1].shifts.map((s) => ({ ...s, break: 1 })),
	},
} as unknown as Record<string, Job>;

const applicationsByJobId: Record<string, ScheduleJobApplications> = {
	"job-1": {
		wouldBookShiftIds: ["s2", "s6", "s7"],
		applications: [
			{
				id: "p1",
				status: "PENDING_ANSWER",
				consultant: { id: "u3", firstName: "Sara", lastName: "Lind", hasCustomerExperience: true, profile: { address: { city: "Uppsala" } } },
			},
			{
				id: "p2",
				status: "PENDING_ANSWER",
				consultant: { id: "u4", firstName: "Omar", lastName: "Haddad", profile: { address: { city: "Knivsta" } } },
			},
			{
				id: "a1",
				status: "ACCEPTED",
				consultant: { id: "u1", firstName: "Elina", lastName: "Mannerstål" },
				shifts: [{ id: "s1" }, { id: "s3" }, { id: "s4" }, { id: "s5" }],
				metadata: { acceptedBy: { id: "cu1", firstName: "Peder", lastName: "Hagen" }, acceptedAt: day(-3, "10:00") },
			},
		],
		invitations: [
			{ id: "i1", status: "OPEN", consultant: { id: "u5", firstName: "Maja", lastName: "Ström", profile: { address: { city: "Uppsala" } } } },
		],
	},
	"job-2": {
		wouldBookShiftIds: [],
		applications: [
			{
				id: "a2",
				status: "ACCEPTED",
				consultant: { id: "u2", firstName: "Johan", lastName: "Berg" },
				shifts: [{ id: "s8" }, { id: "s9" }, { id: "s10" }],
			},
		],
		invitations: [{ id: "i2", status: "DECLINED", consultant: { id: "u2", firstName: "Johan", lastName: "Berg" }, shifts: [{ id: "s11" }] }],
	},
};

const matchingConsultants: ScheduleMatchingConsultant[] = [
	{ id: "u3", firstName: "Sara", lastName: "Lind", hasPendingApplication: true, profile: { address: { city: "Uppsala" } } },
	{ id: "u5", firstName: "Maja", lastName: "Ström", isInvited: true, profile: { address: { city: "Uppsala" } } },
	{ id: "u6", firstName: "Karin", lastName: "Holm", hasCustomerExperience: true, profile: { address: { city: "Enköping" } } },
	{ id: "u7", firstName: "David", lastName: "Nyström", profile: { address: { city: "Stockholm" } } },
	{ id: "u8", firstName: "Lina", lastName: "Ek", hasCustomerExperience: true, profile: { address: { city: "Uppsala" } } },
];

/**
 * Week schedule on fixture data (no API calls). Shift changes are applied to
 * local state so the right-click actions can be tried out.
 */
const WeekScheduleExample = () => {
	const toast = useToast();
	const [jobs, setJobs] = useState(initialJobs);

	return (
		<div className="min-h-screen bg-[#edeff3] p-6">
			<WeekSchedule
				jobs={jobs}
				detailsByJobId={detailsByJobId}
				applicationsByJobId={applicationsByJobId}
				competenceLabelByKey={new Map([["preschool-teacher", "Vikarie förskola"], ["cook", "Kock"]])}
				startWeek={moment().startOf("isoWeek").subtract(1, "week").format("YYYY-MM-DD")}
				endWeek={moment().startOf("isoWeek").add(2, "week").format("YYYY-MM-DD")}
				// The fixtures cover a fixed range of weeks
				onReachStart={() => undefined}
				onReachEnd={() => undefined}
				loadConsultants={async () => matchingConsultants}
				onUpdateShifts={async (jobId, message, change) => {
					setJobs((prev) =>
						prev.map((job) =>
							job.id === jobId
								? {
										...job,
										// Like the backend: added shifts on a job with a booked consultant become an extension request
										shifts: change(job.shifts).map(
											(s, i): WeekScheduleShift =>
												s.id
													? { ...s, id: s.id }
													: {
															...s,
															id: `new-${Date.now()}-${i}`,
															status: job.applications?.length ? "EXTEND_REQUESTED" : undefined,
														},
										),
									}
								: job,
						),
					);
					toast({ title: message, status: "success", variant: "subtle" });
					return true;
				}}
				onInvite={async (_jobId, consultantIds) => {
					toast({
						title: consultantIds.length === 1 ? "Inbjudan har skickats till konsulten" : "Inbjudningarna har skickats",
						status: "success",
						variant: "subtle",
					});
				}}
				onBook={() => toast({ title: "Konsulten är bokad", status: "success", variant: "subtle" })}
			/>
		</div>
	);
};

export default WeekScheduleExample;

/**
 * The single-job view on fixture data: one job in draft mode, where clicking
 * empty days adds drafts that a bar at the bottom sends together.
 */
export const JobScheduleExample = () => {
	const toast = useToast();
	const [job, setJob] = useState(initialJobs[1]);
	const [drafts, setDrafts] = useState<WeekScheduleDraft[]>([]);
	const ref = useRef<WeekScheduleHandle>(null);
	const consultant = getExtensionConsultant(job);

	return (
		<div className="min-h-screen bg-[#edeff3] p-6 pb-28">
			<WeekSchedule
				ref={ref}
				jobs={[job]}
				detailsByJobId={detailsByJobId}
				applicationsByJobId={applicationsByJobId}
				competenceLabelByKey={new Map([["cook", "Kock"]])}
				startWeek={moment().startOf("isoWeek").subtract(1, "week").format("YYYY-MM-DD")}
				endWeek={moment().startOf("isoWeek").add(4, "week").format("YYYY-MM-DD")}
				onReachStart={() => undefined}
				onReachEnd={() => undefined}
				loadConsultants={async () => matchingConsultants}
				onUpdateShifts={async (_jobId, message, change) => {
					setJob((prev) => ({
						...prev,
						shifts: change(prev.shifts).map(
							(s, i): WeekScheduleShift =>
								s.id ? { ...s, id: s.id } : { ...s, id: `new-${Date.now()}-${i}`, status: "EXTEND_REQUESTED" },
						),
					}));
					toast({ title: message, status: "success", variant: "subtle" });
					return true;
				}}
				onInvite={async () => undefined}
				onBook={() => undefined}
				drafts={drafts}
				onDraftsChange={setDrafts}
				showJobScheduleLink={false}
			/>
			{drafts.length > 0 && (
				<div className="fixed bottom-6 left-1/2 flex w-[calc(100%-2rem)] max-w-3xl -translate-x-1/2 items-center gap-4 rounded-2xl border border-solid border-gray-200 bg-white px-5 py-3 shadow-lg">
					<div className="flex-1 text-sm">
						<div className="font-semibold">{drafts.length === 1 ? "1 nytt pass" : `${drafts.length} nya pass`}</div>
						<div className="text-xs text-gray-500">{consultant && `Förfrågan skickas till ${getConsultantName(consultant)}`}</div>
					</div>
					<button type="button" className="cursor-pointer text-sm underline" onClick={() => setDrafts([])}>
						Ångra
					</button>
					<button
						type="button"
						className="cursor-pointer rounded-[14px] !bg-[#2A2A2A] !px-4 !py-2 text-sm !text-white"
						onClick={() => ref.current?.submitDrafts()}
					>
						Sänd förfrågan
					</button>
				</div>
			)}
		</div>
	);
};
